/**
 * The single boundary between the physics frame and the Three.js frame.
 *
 * Physics uses Rocket League's convention: right-handed, +Z up, +Y toward the
 * orange goal (see src/physics/math.ts). Three.js is right-handed with +Y up.
 *
 * The mapping is a -90 degree rotation about X:
 *
 *     three.x =  rl.x
 *     three.y =  rl.z
 *     three.z = -rl.y
 *
 * Converting here and only here means no physics code ever imports Three.js,
 * and no rendering code ever has to reason about which "up" it is holding.
 * Every conversion in the project must go through these functions — a
 * hand-written axis swap somewhere in a mesh builder is how the ball ends up
 * bouncing sideways in the corner and nowhere else.
 */

import { Matrix4, Quaternion, Vector3 } from 'three';
import { col, type Mat3, type Vec3 } from '../physics/math';

/** Physics position/direction -> Three.js. */
export const toThree = (v: Vec3, out = new Vector3()): Vector3 => out.set(v.x, v.z, -v.y);

/** Three.js -> physics. The exact inverse of `toThree`. */
export const fromThree = (v: Vector3): Vec3 => ({ x: v.x, y: -v.z, z: v.y });

/**
 * Physics orientation matrix (columns = forward, left, up) -> Three.js
 * quaternion.
 *
 * Three.js object convention is that the mesh's local -Z points "forward" and
 * +Y is "up". The physics forward/up axes are converted individually and a
 * basis is built from them, so the two conventions are reconciled once here
 * rather than by sprinkling `rotateY(Math.PI)` around the scene graph.
 */
const _axisZ = new Vector3();
const _axisY = new Vector3();
const _axisX = new Vector3();
const _basis = new Matrix4();

export const orientationToQuaternion = (m: Mat3, out = new Quaternion()): Quaternion => {
  // makeBasis takes the three axes as COLUMNS, so the matrix maps
  // (1,0,0) -> X, (0,1,0) -> Y, (0,0,1) -> Z. We want (0,0,-1) (Three's
  // mesh-forward) to land on the physics forward axis, hence Z = -forward,
  // and (0,1,0) to land on the physics up axis, hence Y = up.
  toThree(col(m, 0), _axisZ).normalize().negate();
  toThree(col(m, 2), _axisY).normalize();

  // Re-orthogonalize Y against Z so a slightly non-orthonormal input still
  // yields a proper rotation rather than a shear.
  _axisY.addScaledVector(_axisZ, -_axisY.dot(_axisZ)).normalize();

  // X = Y x Z keeps the basis right-handed (det = +1).
  _axisX.crossVectors(_axisY, _axisZ).normalize();

  _basis.makeBasis(_axisX, _axisY, _axisZ);
  return out.setFromRotationMatrix(_basis);
};

/**
 * Linear interpolation between two physics positions, emitted directly into a
 * Three.js vector. Used every frame by the renderer with the loop's `alpha`.
 */
export const lerpToThree = (a: Vec3, b: Vec3, t: number, out = new Vector3()): Vector3 =>
  out.set(a.x + (b.x - a.x) * t, a.z + (b.z - a.z) * t, -(a.y + (b.y - a.y) * t));
